/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef, useCallback } from "react"
import { Idle } from "uikit"
import { Redirect } from "react-router-dom"
import RenewSessionModal from "./RenewSessionModal"
import Signin from "./Signin"
import { useStore } from "store"
import * as Sentry from "@sentry/react"

const timerProps = {
  defaultIdleTimeout: 1000 * 60 * 60,
  defaultNotificationOffset: 1000 * 20,
  defaultRenewOffset: 1000 * 60 * 5
}

const AuthenticatedView = props => {
  const { location } = props
  const { hash, pathname, state: locationState } = location
  const autoRenewTimer = useRef(null)
  const [state, setState] = useState({
    historyUnlisten: null,
    isAuthenticated: false,
    isPending: false,
    error: "",
    user: {},
    isModalOpen: false,
    idleTimeout: timerProps.defaultIdleTimeout,
    isTokenSignin: null,
    signinToPath: null,
    locationHash: null
  })
  // props.getCurrentUser()
  const [currentUser] = useStore("user")
  const [
    newAgreements,
    {
      loadUser,
      renewToken,
      signinWithToken,
      signOut,
      signinWithPassword,
      validateMfa,
      resendSecurityCode
    }
  ] = useStore("newAgreements")

  const configureServices = useCallback(user => {
    if (process.env.REACT_APP_DEPLOY_ENV) {
      Sentry.configureScope(scope => {
        scope.setUser({
          email: user.email,
          id: user.id
        })
      })
    }
    if (process.env.REACT_APP_DEPLOY_ENV && !!window.gtag) {
      window.gtag("set", { user_id: user.id })
    }
    if (!!window.Intercom) {
      window.trackEvent = (name, metadata) => {}
      if (!user.id) {
        Sentry.captureMessage(`Unknown user: ${user.email}`, "warning")
      } else {
        window.Intercom("boot", {
          app_id: process.env.REACT_APP_INTERCOM_APP_ID,
          name: user.first_name + " " + user.last_name,
          email: user.email,
          user_id: user.id,
          group_id: user.group_id,
          channel_id: user.channel_id,
          custom_launcher_selector: "#intercom"
        })
        window.trackEvent = (name, metadata) => {
          window.Intercom("trackEvent", name, metadata)
        }
      }
    }
    if (state.historyUnlisten) {
      state.historyUnlisten()
    }
    state.historyUnlisten = props.history.listen((location, action) => {
      window.Intercom && window.Intercom("update")
    })
  }, [])

  const getRenewTimeout = useCallback(renewBy => {
    return renewBy.getTime() - Date.now() - timerProps.defaultRenewOffset
  }, [])

  const getIdleTimeout = useCallback((renewedAt, renewBy) => {
    const timeSinceRenew = Date.now() - renewedAt.getTime()
    if (timeSinceRenew > timerProps.defaultNotificationOffset) {
      return timerProps.defaultIdleTimeout
    }
    return getRenewTimeout(renewBy) - timerProps.defaultNotificationOffset
  }, [])
  const setAutoRenewTimer = useCallback(renewBy => {
    if (autoRenewTimer.current) {
      clearTimeout(autoRenewTimer.current)
    }
    autoRenewTimer.current = setTimeout(
      renewTokenCallback,
      getRenewTimeout(renewBy)
    )
  }, [])

  const updateSessionState = useCallback(async (extra = {}) => {
    const sessionData = JSON.parse(localStorage.getItem("sessionData"))
    if (!sessionData) {
      setState(cur => ({ ...cur, isAuthenticated: false, isPending: false }))
      return
    }
    const { renewed_at, renew_by, expires_at } = sessionData
    if (new Date(expires_at) < new Date()) {
      logout()
      return
    }
    setState(cur => ({
      ...cur,
      isAuthenticated: true,
      idleTimeout: getIdleTimeout(new Date(renewed_at), new Date(renew_by)),
      isModalOpen: false,
      ...extra
    }))
    setAutoRenewTimer(new Date(renew_by))
    let user = state.user.id ? state.user : currentUser
    if (!!user.id) {
      await loadUser()
      user = currentUser
    }
    configureServices(user)
  }, [])

  const renewTokenCallback = useCallback(() => {
    renewToken()
      .then(() => {
        updateSessionState()
      })
      .catch(() => {
        setState(cur => ({
          ...cur,
          isAuthenticated: false,
          isModalOpen: false
        }))
      })
  }, [])

  const onDidMount = useCallback(async () => {
    setState(cur => ({ ...cur, isPending: true }))
    try {
      if (hash) {
        window.localStorage.removeItem("lastLocation")
        await signinWithToken({ token: hash.slice(1) })
        setState(cur => ({
          ...cur,
          isTokenSignin: true,
          signinToPath: pathname
        }))
      }
      const isValidUser = !!currentUser.id
      if (isValidUser) {
        updateSessionState({ currentUser })
      } else {
        onError()
      }
      setState(cur => ({ ...cur, isPending: false }))
    } catch (error) {
      setState(cur => ({ ...cur, isPending: false }))
    }
  }, [hash])

  useEffect(() => {
    onDidMount()
    if (!state.locationHash) {
      setState(cur => ({ ...cur, locationHash: hash }))
    } else if (state.locationHash !== hash) {
      setState(cur => ({ ...cur, isTokenSignin: false, locationHash: hash }))
    }

    return () => {
      if (state.historyUnlisten) {
        state.historyUnlisten()
      }
      if (autoRenewTimer.current) {
        clearInterval(autoRenewTimer.current)
      }
    }
  }, [hash])

  const logout = useCallback(() => {
    state.historyUnlisten && state.historyUnlisten()
    window.Intercom("shutdown")
    setState(cur => ({ ...cur, isAuthenticated: false, isModalOpen: false }))
    signOut()
    props.history.push("/")
  }, [])

  const onError = useCallback(() => {
    if (autoRenewTimer.current) {
      clearTimeout(autoRenewTimer.current)
    }
    logout()
  }, [])

  const signin = useCallback(
    async ({ email, password }) => {
      const deviceId = window.localStorage.getItem("deviceUuid")
      const response = await signinWithPassword({
        email,
        password,
        deviceId
      })
      if (!response.error && response.next_step === "finalize") {
        updateSessionState({ currentUser })
      }
      return response
    },
    [currentUser]
  )

  const validateMfaCallback = useCallback(
    async ({ mfaCode, token }) => {
      const deviceId = window.localStorage.getItem("deviceUuid")
      const response = await validateMfa({
        mfaCode,
        token,
        deviceId
      })
      if (!response.error && response.next_step === "finalize") {
        updateSessionState({ currentUser })
      }
      return response
    },
    [currentUser]
  )

  const resendSecurityCodeCallback = useCallback(async token => {
    const resp = await resendSecurityCode(token)
    return resp
  }, [])

  return (
    <Idle
      timeout={state.idleTimeout}
      onChange={({ idle }) => {
        if (idle && state.isAuthenticated) {
          if (autoRenewTimer.current) {
            clearTimeout(autoRenewTimer.current)
          }
          if (!state.isModalOpen) {
            setState(cur => ({ ...cur, isModalOpen: true }))
          }
        }
      }}
      render={({ idle }) => {
        if (state.isPending) {
          return <div>Checking... </div>
        }
        if (state.isModalOpen) {
          return (
            <RenewSessionModal
              onYes={() => renewTokenCallback()}
              onNo={() => logout()}
              autoSignoutTimeout={timerProps.defaultRenewOffset}
            />
          )
        }
        if (state.isAuthenticated) {
          if (state.isTokenSignin) {
            return <Redirect to={state.signinToPath} />
          }

          if (newAgreements.length > 0 && !pathname.includes("/agreements")) {
            return <Redirect to="/agreements" />
          }
          return props.render({
            logout: logout
          })
        } else {
          const signinState = locationState || undefined
          return (
            <Signin
              submit={signin}
              validateMfa={validateMfaCallback}
              signinState={signinState}
              resendSecurityCode={resendSecurityCodeCallback}
            />
          )
        }
      }}
    />
  )
}

export default AuthenticatedView
